/**
 * Simplified Chinese translation strings
 * Used throughout the application for UI text, pages, and messages
 *
 * This is the framework template with common, auth, wallet, and navigation strings.
 * Business-specific translation keys should be added per feature module requirements.
 */
export const zh = {
  welcome: {
    title: "你的资金，\n你来掌控。",
    description: "聊天、支付、交易。由你决定，Agent 协助。",
    create: "创建钱包",
    existing: "我已有钱包",
    security: "密钥由你保管，Agent 的权限由你决定。",
    language: "切换语言",
    restoring: "正在打开你的空间…",
    authenticating: "请核对登录请求后继续。",
    name: "钱包名称",
    optional: "选填",
    namePlaceholder: "我的钱包",
    setupTitle: "从这里，开始。",
    setupDescription: "在这台设备上创建钱包，并妥善保管恢复助记词。",
    deviceTitle: "由你的设备保护",
    deviceDescription: "设备会要求验证两次：先创建凭据，再加密钱包。",
    backupTitle: "留好一份恢复凭证",
    backupDescription: "接下来保存恢复助记词，再核对你的记录。",
    createWithDevice: "验证设备并创建",
    checking: "正在检查设备支持…",
    unsupported: "此浏览器不支持设备验证，可选择其他可用的钱包方式。",
    limit: "这台设备的钱包数量已达上限，请选择已有钱包。",
    checkFailed: "暂时无法检查设备支持，请重试或选择其他钱包方式。",
    retry: "重新检查",
    stepCredential: "创建安全凭据",
    stepEncryption: "加密你的钱包",
    credentialDetail: "请确认设备显示的验证请求。",
    encryptionDetail: "请再次验证，以加密钱包密钥。",
    actualProgress: "跟随设备提示操作，页面会按实际步骤更新。",
    recoveryWarning: "请按顺序保管这些词，不要分享。持有它们的人可以控制你的钱包。",
    recoveryCopy: "复制助记词",
    recoveryCopied: "已复制",
    recoveryContinue: "核对我的记录",
    verifyTitle: "核对你的恢复凭证。",
    verifyDescription: "输入指定位置的词，确认你的记录准确无误。",
    word: "第 {{number}} 个词",
    verifyComplete: "完成核对",
    copiedFailure: "未能复制，请抄写保存后再继续。",
    createFailed: "钱包创建未完成，已有钱包没有被移除。",
    previewNote: "设计预览，不创建钱包、设备凭据或恢复助记词。",
  },
  // Brand identity
  brand: {
    name: "DeTrust",
    tagline: "你的专属链上 AI 代理",
    logoLabel: "应用标志",
  },

  // Common UI elements
  common: {
    welcome: "欢迎",
    loading: "加载中...",
    error: "错误",
    success: "成功",
    confirm: "确认",
    cancel: "取消",
    save: "保存",
    delete: "删除",
    edit: "编辑",
    close: "关闭",
    back: "返回",
    next: "下一步",
    previous: "上一步",
    submit: "提交",
    search: "搜索",
    filter: "筛选",
    clear: "清除",
    all: "全部",
    none: "无",
    yes: "是",
    no: "否",
    optional: "可选",
    required: "必填",
    comingSoon: "即将推出",
    popular: "热门",
    noData: "暂无数据",
    copyAddress: "复制地址",
    copied: "已复制!",
    disconnect: "断开连接",
    connect: "连接",
    viewDetails: "查看详情",
    learnMore: "了解更多",
    retry: "重试",
    of: "/",
    searchCountry: "搜索国家...",
    unknownError: "发生未知错误",
  },

  // Navigation
  nav: {
    home: "首页",
    chat: "对话",
    dashboard: "总览",
    strategies: "策略",
    settings: "设置",
    profile: "个人资料",
    help: "帮助",
    docs: "文档",
  },

  // Authentication and wallet
  auth: {
    connectWallet: "连接钱包",
    walletConnected: "钱包已连接",
    walletDisconnected: "钱包已断开",
    selectWallet: "选择您的钱包",
    installedWallets: "已安装的钱包",
    otherWallets: "其他钱包",
    connectingWallet: "正在连接钱包...",
    disconnectWallet: "断开钱包",
    wrongNetwork: "网络错误",
    switchNetwork: "切换网络",
    walletRequired: "需要连接钱包",
    connectToView: "请连接钱包以查看此页面",
    noWalletDetected: "未检测到钱包",
    installWallet: "请安装Solana钱包",
    copyAddressSuccess: "地址已复制到剪贴板",
    // LoginButton specific
    signIn: "登录",
    signingIn: "正在登录...",
    connecting: "连接中...",
    connectedWallet: "已连接钱包",
    loggedInAs: "已登录为 {{address}}。点击打开钱包菜单",
    connectWalletToSignIn: "连接Solana钱包以登录",
    signInWithConnectedWallet: "使用已连接的钱包登录",
    logoutFromWallet: "从钱包登出",
    logout: "登出",
    loggingOut: "正在登出...",
    reconnectWallet: "重新连接钱包",
    reconnectWalletDesc: "您的钱包已断开连接。请重新连接以继续。",
    connectAndSignIn: "连接并登录",
  },

  // Error messages
  errors: {
    generic: "发生错误，请重试。",
    networkError: "网络错误，请检查您的连接。",
    walletConnection: "钱包连接失败",
    walletDisconnection: "钱包断开失败",
    transactionRejected: "交易被用户拒绝",
    transactionFailed: "交易失败",
    insufficientFunds: "资金不足",
    invalidAmount: "无效金额",
    invalidAddress: "无效地址",
    slippageExceeded: "价格滑点超出容忍范围",
    deadline: "交易截止时间已过",
    gasEstimation: "Gas估算失败",
    contractError: "智能合约错误",
    notFound: "资源未找到",
    unauthorized: "未授权访问",
    forbidden: "禁止访问",
    validationError: "验证错误",
    serverError: "服务器错误，请稍后重试。",
    timeout: "请求超时",
  },

  // Success messages
  success: {
    walletConnected: "钱包连接成功",
    walletDisconnected: "钱包断开成功",
    transactionSubmitted: "交易已提交",
    transactionConfirmed: "交易已确认",
    settingsSaved: "设置保存成功",
    profileUpdated: "个人资料更新成功",
    copied: "已复制到剪贴板",
  },

  // Footer
  footer: {
    tagline: "基于 Solana 构建",
    product: "产品",
    features: "功能",
    documentation: "文档",
    company: "公司",
    about: "关于我们",
    blog: "博客",
    careers: "招聘",
    contact: "联系",
    legal: "法律",
    privacy: "隐私政策",
    terms: "服务条款",
    copyright: "2026 DeTrust. 保留所有权利。",
  },

  // Wallet connection
  wallet: {
    connecting: "连接中...",
    connectWallet: "连接钱包",
    copyAddress: "复制地址",
    viewOnExplorer: "在浏览器中查看",
    disconnect: "断开连接",
    paymentToken: "USDC",
    secondaryToken: "TOKEN",
    balance: "余额",
    refresh: "刷新",
    transfer: "转账",
    send: "发送",
    selectToken: "选择代币",
    confirmInWallet: "请在钱包中确认...",
    viewTransaction: "查看交易",
    done: "完成",
    tryAgain: "重试",
    solReserveHint: "保留0.01 SOL作为手续费",
    pasteAddress: "粘贴",
    recipientAddress: "收款地址",
    amount: "金额",
    maxAmount: "最大",
    sending: "发送中...",
    invalidAddress: "无效的Solana地址",
    insufficientBalance: "余额不足",
    selfTransferError: "不能发送给自己",
    transferBalanceRefreshUnavailable: "转账已确认，但余额显示暂时无法刷新。",
    transferAuthority: "这笔转账由你的钱包签名",
    transferUnknown: "交易结果尚未确认。再次发送前，请先查询原交易。",
    transferRecoveryUnavailable: "暂时无法读取原交易状态。核实前已暂停再次发送。",
    checkOriginalTransaction: "查询原交易",
    checkingOriginalTransaction: "正在查询原交易…",
    transferFailedOnChain: "这笔交易已确认在链上失败。",
    transferNotSubmitted: "交易尚未提交。",
    transferSuccess: "转账成功",
    available: "可用",
  },

  // Login modal
  loginModal: {
    signInToApp: "登录",
    securelyAuthenticate: "使用您的Solana钱包安全认证",
    reviewSignatureRequest: "审核签名请求",
    reviewMessageBeforeSigning: "签名前请审核以下消息",
    signingMessage: "签名消息",
    completeSignatureRequest: "请在钱包中完成签名请求",
    success: "成功!",
    youAreSignedIn: "您已成功登录",
    error: "错误",
    somethingWentWrong: "出现问题",
    secureConnection: "安全连接",
    connectYourWallet: "连接您的钱包",
    connectToSignIn: "连接您的Solana钱包安全登录。您的钱包将用于验证身份。",
    encrypted: "加密",
    secure: "安全",
    yourKeys: "您的密钥",
    waitingForSignature: "等待签名",
    approveInWallet: "请在钱包中批准签名请求...",
    approveInWalletNamed: "请在{{wallet}}中批准签名请求...",
    secureSigningInProgress: "安全签名进行中",
    selectWallet: "选择您的钱包",
    selectYourWallet: "选择您的钱包",
    chooseWalletFromList: "从Phantom、Solflare、Ledger或其他支持的钱包中选择",
    pickFromSupportedWallets: "从20+支持的钱包中选择，包括硬件钱包",
    preparingSignature: "准备签名请求...",
    signingIn: "登录中",
    preparingAuth: "准备认证...",
    authenticationSuccessful: "认证成功!",
    successfullyAuthenticated: "您已成功通过认证。",
    authenticationFailed: "认证失败",
    errorOccurred: "认证过程中发生错误。请重试。",
    cancel: "取消",
    connectWalletBtn: "连接钱包",
    signMessage: "签名消息",
    checkWalletForSignature: "请检查您的钱包中的签名请求...",
    close: "关闭",
    tryAgain: "重试",
  },

  // Desktop gate
  desktopGate: {
    title: "仅限桌面端",
    description:
      "此应用程序针对桌面浏览器进行了优化。请使用屏幕宽度至少为1024px的设备。",
  },

  // 对话（vNext 上的会话 — 规范 2026-09-06 §3.2，任务 4）
  chat: {
    signing: {
      authority: "由你的钱包批准本次操作",
      title: "钱包签名请求",
      connectOwner: "请连接此请求所属的钱包。",
      unavailable: "暂时无法读取请求状态",
      loading: "正在读取本次操作…",
      prepared: "等待你的签名",
      submitted: "已提交，等待回执",
      unknown: "结果尚未确认",
      confirmed: "已确认",
      finalized: "已最终确认",
      failed: "链上执行失败",
      rejected: "请求已拒绝",
      expired: "准备的交易已过期",
      working: "正在处理…",
      wallet: "签名钱包",
      programs: "交易程序",
      signature: "原交易签名",
      keepOriginal: "请继续核对此操作；不会自动构建或签名替代交易。",
      sign: "查看并签名",
      reject: "拒绝未签名请求",
      check: "查询本次操作",
      retrySigned: "重试原已签名交易",
      listUnavailable: "暂时无法读取签名请求，这不代表没有待处理请求。",
    },
    sidebar: {
      title: "会话",
      newConversation: "新会话",
      search: "搜索会话",
      searchPlaceholder: "搜索会话…",
      loading: "正在加载会话",
      empty: "还没有会话。开始一个吧。",
      noMatch: "没有匹配的会话。",
      noMessages: "还没有消息",
      needsYou: "等你确认",
      pinned: "已置顶",
      actions: "会话操作",
      pin: "置顶",
      unpin: "取消置顶",
      rename: "重命名",
      renameLabel: "新标题",
      save: "保存",
      cancel: "取消",
      markRead: "标为已读",
      archive: "归档",
      groups: {
        pinned: "置顶",
        today: "今天",
        yesterday: "昨天",
        thisWeek: "本周",
        older: "更早",
      },
      category: {
        trading: "交易",
        shopping: "购物",
        defi: "DeFi",
        general: "通用",
        notification: "通知",
        task: "任务",
      },
    },
    time: {
      justNow: "刚刚",
      minutes: "{{n}} 分钟前",
      hours: "{{n}} 小时前",
      days: "{{n}} 天前",
      yesterday: "昨天",
    },
    thread: {
      title: "Agent 对话",
      you: "你",
      agent: "Agent",
      clear: "清除",
      emptyTitle: "和你的个人 Agent 开始对话。",
      emptyHint: "问问价格、持仓、风险分析或交易策略。",
      connecting: "连接中…",
      offline: "离线 — 正在重连",
      connectionError: "连接出错",
      using: "正在使用 {{skill}}…",
    },
    composer: {
      label: "消息",
      placeholder: "发送消息…",
      thinking: "Agent 正在思考…",
      send: "发送消息",
      notSent: "没有发出 — 你已离线。草稿仍在。",
      quick: {
        portfolio: "持仓",
        portfolioText: "看看我的持仓",
        goldPrice: "金价",
        goldPriceText: "现在金价多少？",
        strategies: "我的策略",
        strategiesText: "看看我运行中的策略",
        riskProfile: "风险偏好",
        riskProfileText: "看看我的风险偏好",
      },
    },
    bubble: {
      noResponse: "我没能生成回复，请再试一次。",
    },
    context: {
      panel: "上下文",
      awaiting: "正在等待 Agent 的快照…",
      spendingRoom: "今日可支出额度",
      spendingRoomHint: "在你的规则下，Agent 今天还能花的钱。不是余额。",
      spendingRoomUnknown: "本次连接尚未收到",
      ledger: "持仓账本",
      ledgerReady: "已是最新",
      ledgerHeld: "已冻结 — 正在追赶链上状态",
      ledgerUnknown: "未上报",
      decisions: "等你处理",
      noDecisions: "没有需要你处理的事。",
      review: "查看",
      decisionTransaction: "一笔交易需要你批准",
      decisionTransfer: "一笔给他人的付款需要你批准",
      decisionOther: "{{type}} 需要你批准",
      amountInThread: "金额见会话内",
      tier: "T{{tier}}",
      strategies: "运行中的策略",
      noStrategies: "没有运行中的策略",
      strategyActive: "运行中",
      strategyPaused: "已暂停",
      strategyTriggered: "已触发",
      strategyError: "失败",
      incidents: "未关闭的事件",
      noIncidents: "没有未关闭的事件",
    },
    approval: {
      approvalRequired: "需要你批准",
      withinRules: "在你的规则内",
      cancellable: "已完成，仍可撤销",
      doneUnderRules: "已按你的规则完成",
      approvedByYou: "由你批准",
      rejected: "已被你拒绝",
      expired: "在你决定前已过期",
      revoked: "授权已撤回",
      simulated: "已模拟 — 没有发出任何交易",
      failed: "交易失败。这次操作已用掉；没有可重试的内容。",
      awaitingReceipt: "流上说已结算，但今天的时间线里还没有找到这张卡自己的回执。",
      foreignReceipt: "给出的事件不是这张卡自己的结算回执。",
      signatureDiffers: "回执指向的交易与这张卡发出的不是同一笔。",
      legacyConfirmed: "这是结算与发送尚未分离时写下的记录；请核验它的回执。",
      notRecorded: "回执上未记录",
    },
    card: {
      agentWallet: "Agent 钱包",
      decline: "拒绝",
      approve: "批准",
      checkReceipt: "核验回执",
      viewReceipt: "查看回执",
      viewTransaction: "查看交易",
      quoteDetails: "报价详情",
      youReceive: "你将收到",
      rate: "汇率",
      priceImpact: "价格影响",
      minReceived: "最少收到",
      gas: "网络费用",
      quoteValid: "报价有效 {{time}}",
      quoteStale: "这份报价已过时。等待服务器关闭它；不能再据此批准。",
      nothingMoved: "没有任何资金变动。这是当时的报价。",
      warnings: "警告",
      contractWhitelisted: "合约在白名单内",
      simulationPassed: "模拟通过",
      noUnlimitedApproval: "没有无限授权",
      evidenceChainValid: "证据链有效",
      actNotSent: "没有发出 — 你已离线。重试会用同一个决定，只发一次。",
      actUnknown: "你的决定还没有回音。再次决定前先核验它的回执。",
      retry: "重试",
      reported: "已上报",
      reportedHint: "由 Agent 上报；今天的时间线里还没有找到它自己的结算回执。",
      total: "合计",
      fee: "费用",
      delivery: "配送",
      order: "订单",
      orderPlaced: "订单已下",
      orderProcessing: "订单处理中",
      orderFailed: "订单失败",
      ifWrong: "如果出了问题",
      irreversible: "不可撤销",
      waitingOnYou: "等你决定",
      doneCancellable: "已完成，仍可撤销",
      withinRules: "在你的规则内",
      blocked: "已阻止",
      byAgent: "由你的 Agent 执行",
    },
  },

  // Dashboard (vNext page migration, Task 5)
  dashboard: {
    title: "总览",
    subtitle: "今天回来了什么，以及代理的状态",
    loading: "正在加载总览...",
    loadFailed: "总览数据加载失败。",
    checkConnection: "请检查网络连接后重试。",
    portfolio: {
      title: "资产估值",
      unavailable: "金额不可用",
      readFailed: "暂时无法读取你的余额。没有任何变化；请稍后再试。",
      solHeld: "持有 SOL",
    },
    strategies: {
      title: "运行中的策略",
      count: "共 {{count}} 个",
      empty: "还没有配置策略。",
      lastTriggered: "上次触发",
      never: "从未",
      executions: "执行次数",
      status: {
        draft: "草稿",
        active: "运行中",
        paused: "已暂停",
        triggered: "已触发",
        completed: "已完成",
        failed: "失败",
      },
    },
    freshness: {
      title: "上下文新鲜度",
      notReported: "未报告",
      good: "良好",
      stale: "陈旧",
      critical: "严重",
    },
    health: {
      title: "代理健康",
      skills: "技能",
      healthy: "{{healthy}}/{{total}} 正常",
      entropy: "熵",
      anchorsAge: "锚点时效",
      hours: "{{hours}} 小时",
      notReported: "未报告",
    },
    quickActions: {
      title: "快捷操作",
      chat: "对话",
      newStrategy: "新建策略",
      portfolio: "资产",
      settings: "设置",
    },
    feed: {
      title: "回来了什么",
      count: "{{count}} 条",
      empty: "还没有任何结果回来。",
      loadFailed: "今天的回执无法读取。",
      retry: "重试",
      refresh: "刷新",
      recorded: "已记录",
    },
  },

  // Agent wallet (vNext page migration, Task 5)
  agentWallet: {
    title: "代理钱包",
    subtitle: "代理在你设定的规则下用来签名的钱包",
    loading: "正在读取代理钱包...",
    notInitialised: "无法加载代理钱包。代理可能尚未初始化。",
    authority: "代理在你设定的规则下用这个钱包签名",
    address: "充值地址",
    copy: "复制地址",
    copied: "已复制",
    trustNote:
      "这个钱包由你的代理持有。只存入你愿意让它支配的金额；余额就是硬性上限。",
    sweep: {
      title: "归集到你的钱包",
      description:
        "把代理钱包里的 SOL 转到你指定的地址。会先征求你的确认，只凭它自己的回执判定完成。",
      destinationLabel: "目标地址",
      destinationPlaceholder: "一个 Solana 地址",
      invalidDestination: "这不是一个 Solana 地址。",
      review: "核对归集",
      reviewing: "正在读取余额",
      balanceReadFailed: "无法读取余额。没有发送任何交易。",
      walletNotReady: "代理钱包尚未就绪。",
      needsYou: "需要你确认",
      approved: "已由你确认",
      recipient: "收款地址",
      account: "代理钱包",
      reserveNote:
        "代理钱包的余额。服务器会保留少量作为手续费与租金；实际转出金额以回执为准。",
      confirm: "确认归集",
      edit: "修改",
      check: "核验回执",
      startOver: "重新开始",
      startOverNote:
        "重新开始不会发送任何交易。再次核对会重新读取代理钱包余额——这才是判断上一次尝试是否动了钱的依据。",
      receipt: "查看回执",
      transactionFailed: "这笔归集交易已在链上失败。",
      submissionUnknown: "归集结果未知。开始另一笔归集前，请先核验原操作。",
      sentAs: "交易签名 {{signature}}。尚未找到它自己的回执（sweep.settled）。",
      notRecorded: "服务器已发送但未能记录：{{warning}}",
      foreignReceipt: "找到的结算事件不是这次归集自己的回执。",
      signatureDiffers: "回执指向的交易与这次归集发送的不一致。",
      sendFailed: "归集未能发送。",
    },
    log: {
      title: "最近执行",
      empty: "还没有执行记录。",
      settled: "已结算",
      reported: "已上报",
      unknown: "结果未知",
      failed: "失败",
    },
    triggers: {
      title: "定时触发器",
      count: "{{count}} 个运行中",
      empty: "没有运行中的触发器。可通过对话或 API 创建。",
      active: "运行中",
      paused: "已暂停",
      next: "下次",
      fired: "已触发 {{count}} 次",
      pause: "暂停",
      resume: "恢复",
      cancel: "取消",
    },
  },

  // ── vNext page migration, Task 6: strategies, agent settings, bind, CLI auth ──
  strategy: {
    title: "策略",
    subtitle: "代理替你执行的规则",
    newStrategy: "新建策略",
    createStrategy: "创建策略",
    createTitle: "创建策略",
    editTitle: "编辑策略",
    createSubtitle: "用自然语言描述你的策略，或从模板开始。",
    editSubtitle: "修改策略的参数和规则。",
    emptyTitle: "还没有策略",
    emptyDetail: "创建你的第一个自动化策略。可以用自然语言描述，也可以从预置模板开始。",
    backToStrategies: "返回策略列表",
    edit: "编辑",
    executions: "评估次数",
    lastRun: "上次条件匹配",
    never: "从未",
    created: "创建于",
    lastUpdated: "最近更新",
    rules: "策略规则",
    backtest: "回测",
    backtestUnavailable: "回测功能尚未开放。",
    executionHistory: "评估记录",
    historyUnavailable: "无法加载评估记录。",
    executionNote:
      "每一行是代理自身的评估记录，不是回执。已结算的资金会以其自身事件出现在总览页。",
    noExecutions: "尚无评估记录。",
    colTime: "时间",
    colAction: "动作",
    colAsset: "资产",
    colAmount: "规则金额",
    colStatus: "状态",
    colReceipt: "回执",
    noReceipt: "此接口不提供",
    viewOnExplorer: "在浏览器查看",
    status: {
      active: "运行中",
      paused: "已暂停",
      draft: "草稿",
      triggered: "已触发",
      completed: "已完成",
      failed: "失败",
    },
    execution: {
      reported: "已上报",
      pending: "待处理",
      failed: "失败",
    },
    template: {
      dca: "定投",
      rsi: "RSI",
      priceTrigger: "价格触发",
      custom: "自定义",
    },
    notFound: "未找到策略",
    notFoundDetail: "此策略不存在。",
    saveStrategy: "保存策略",
    updateStrategy: "更新策略",
    saving: "保存中",
    convert: "转换为策略",
    converting: "转换中",
    amountUsd: "金额",
    amountInvalid: "请输入最多两位小数的美元金额",
    relative: {
      justNow: "刚刚",
      minutes: "{{count}} 分钟前",
      hours: "{{count}} 小时前",
      days: "{{count}} 天前",
    },
  },
  agentSettings: {
    title: "代理设置",
    subtitle: "配置代理的行为、额度和能力。",
    saving: "正在保存设置",
    budget: {
      title: "预算额度",
      description: "代理在一天内无需征求你同意即可花费的上限。",
      sliderLabel: "每日预算",
      dailyLimit: "每日额度",
      setLimit: "设置额度",
      spentToday: "今日已用",
      resetsAt: "重置时间",
      resetNotReported: "未提供重置时间",
      range: "整数美元，{{min}} 至 {{max}}",
      invalid: "请输入 {{min}} 至 {{max}} 之间的整数美元",
    },
    autoApprove: {
      perTransaction: "单笔",
      perDay: "每日",
      enforcedNote:
        "代理强制执行单笔上限和你的每日额度。每日自动批准上限和资产列表随设置保存。",
    },
    aiModel: {
      connected: "已连接",
      invalidKey: "密钥无效",
      disabled: "已停用",
      notConnected: "未连接",
      addKey: "添加 API 密钥",
      edit: "编辑",
      remove: "移除 {{provider}}",
      cancel: "取消",
      saveKey: "保存密钥",
      updateKey: "更新密钥",
    },
    plan: {
      refresh: "刷新套餐",
      loading: "正在核对你的套餐…",
      error: "无法加载套餐信息。",
      retry: "重试",
      proOnly: "Pro 套餐可用。",
    },
    tokens: {
      newToken: "新建令牌",
      createToken: "创建令牌",
      cancel: "取消",
      copy: "复制",
      copied: "已复制",
      saved: "我已保存令牌",
      revoke: "撤销令牌",
      shownOnce: "此令牌仅显示一次。请妥善保存——如果丢失，请撤销后重新创建。",
    },
    export: {
      export: "导出",
      exporting: "导出中",
    },
    anchors: {
      edit: "编辑",
      cancel: "取消",
      save: "保存",
      saving: "保存中",
    },
    skills: {
      browse: "浏览技能",
      working: "处理中",
      health: {
        healthy: "健康状态：正常",
        degraded: "健康状态：降级",
        unhealthy: "健康状态：异常",
      },
      uninstall: "卸载",
      uninstalling: "卸载中",
      cancel: "取消",
      healthy: "{{count}} 个正常",
      degraded: "{{count}} 个降级",
      unhealthy: "{{count}} 个异常",
      install: "安装",
      installed: "已安装",
      builtIn: "内置",
      comingSoon: "即将推出",
    },
  },
  bind: {
    invalidTitle: "链接无效",
    invalidDetail: "此绑定链接无效或不完整。请向你的机器人重新获取链接。",
    connectTo: "连接到 {{platform}}",
    intro: "将你的 Solana 钱包与 {{platform}} 账号关联，以使用你的代理。",
    signInPrompt: "使用钱包登录，即可将其关联到 {{platform}}。",
    platform: "平台",
    platformId: "ID",
    wallet: "钱包",
    linking: "关联中…",
    bound: "已绑定",
    failed: "失败",
    takenElsewhere: "已绑定到其他钱包",
    takenElsewhereDetail:
      "此账号已绑定到另一个钱包。请在那边解绑，或使用持有该绑定的钱包登录。",
    unknown: "结果未知",
    unknownDetail: "服务器已响应，但目前没有记录表明此钱包已完成绑定。",
    checkAgain: "重新检查",
    tryAgain: "重试",
    genericError: "出现问题，请重试。",
    returnTo: {
      telegram: "现在可以回到 Telegram，开始与你的代理对话。",
      discord: "现在可以回到 Discord，使用斜杠命令与你的代理交互。",
      mobile: "现在可以回到应用，开始对话。",
    },
    platformName: {
      telegram: "Telegram",
      discord: "Discord",
      mobile: "移动应用",
    },
  },
  // 外观：跟随设备 / 浅色 / 深色，与语言一样按设备保存（spec 2026-09-07 D18）。
  theme: {
    label: "外观",
    followDevice: "跟随此设备",
    light: "浅色",
    dark: "深色",
    change: "切换外观",
  },
  cliAuth: {
    title: "授权 DeTrust CLI",
    invalidTitle: "无效请求",
    invalidDetail: "此页面必须由以下命令打开：",
    copyIntro: "复制会话令牌并粘贴到终端提示中。令牌在 2 小时内过期。",
    copyToken: "复制令牌",
    copied: "已复制 ✓",
    handing: "正在把会话交给你的终端…",
    authorized: "CLI 已授权",
    done: "现在可以回到你的终端。",
    failed: "无法连接到 CLI",
    failedDetail:
      "无法连接到本机的 CLI（Safari 会阻止此操作——请使用 Chrome/Firefox，或使用下方的复制方式）。",
    thenRun: "然后运行",
    andPaste: "并粘贴。",
  },
};
